import React, { useEffect, useState, useMemo } from 'react';
import { getServices } from '../api';

const ALL = 'All';

export default function StepService({ slug, onSelect }) {
  const [services, setServices] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [activeCategory, setActiveCategory] = useState(ALL);

  useEffect(() => {
    getServices(slug)
      .then(setServices)
      .catch(() => setError('Failed to load services.'))
      .finally(() => setLoading(false));
  }, [slug]);

  const categories = useMemo(() => {
    const cats = [...new Set(services.map(s => s.category).filter(Boolean))].sort();
    return cats.length ? [ALL, ...cats] : [];
  }, [services]);

  const filtered = useMemo(() =>
    activeCategory === ALL
      ? services
      : services.filter(s => s.category === activeCategory),
  [services, activeCategory]);

  if (loading) return (
    <div className="w-loading">
      <div className="w-spinner" />
      <span>Loading services…</span>
    </div>
  );

  if (error) return <div className="w-error">{error}</div>;
  if (!services.length) return <div className="w-empty">No services available.</div>;

  return (
    <>
      <div className="w-title">Choose a service</div>

      {categories.length > 1 && (
        <div className="w-cat-scroll">
          {categories.map(cat => (
            <button
              key={cat}
              className={`w-cat-chip ${activeCategory === cat ? 'active' : ''}`}
              onClick={() => setActiveCategory(cat)}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gap: '0.75rem',
        padding: '0.25rem 0',
      }}>
        {filtered.map(s => (
          <button
            key={s.id}
            onClick={() => onSelect(s)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              textAlign: 'left',
              padding: 0,
              background: '#fff',
              border: '1.5px solid #e5e7eb',
              borderRadius: '0.75rem',
              overflow: 'hidden',
              cursor: 'pointer',
              transition: 'box-shadow 0.15s, border-color 0.15s',
              boxShadow: '0 1px 3px rgba(0,0,0,0.07)',
            }}
            onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.13)'; e.currentTarget.style.borderColor = 'var(--primary)'; }}
            onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.07)'; e.currentTarget.style.borderColor = '#e5e7eb'; }}
          >
            {/* Image */}
            <div style={{ width: '100%', aspectRatio: '4/3', background: '#f3f4f6', overflow: 'hidden', flexShrink: 0 }}>
              {s.image_url ? (
                <img
                  src={s.image_url}
                  alt={s.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  onError={e => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ width: '2.5rem', height: '2.5rem', borderRadius: '50%', background: s.color || 'var(--primary)', opacity: 0.3 }} />
                </div>
              )}
            </div>

            {/* Info */}
            <div style={{ padding: '0.6rem 0.65rem 0.7rem', flex: 1, display: 'flex', flexDirection: 'column', gap: '0.2rem' }}>
              <div style={{ fontWeight: 600, fontSize: '0.82rem', color: '#111', lineHeight: 1.3 }}>{s.name}</div>
              {s.description && (
                <div style={{ fontSize: '0.72rem', color: '#6b7280', lineHeight: 1.4, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                  {s.description}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'auto', paddingTop: '0.35rem' }}>
                <span style={{ fontSize: '0.72rem', color: '#9ca3af' }}>⏱ {s.duration_minutes} min</span>
                {s.price_cents > 0 && (
                  <span style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--primary)' }}>
                    ${(s.price_cents / 100).toFixed(2)}
                  </span>
                )}
              </div>
            </div>
          </button>
        ))}
        {filtered.length === 0 && (
          <div className="w-empty" style={{ gridColumn: '1/-1' }}>No services in this category.</div>
        )}
      </div>
    </>
  );
}
